import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UsageBadge } from "@/components/UsageBadge";
import { TABS } from "@/components/booking-form/registry/tabs";
import { RotateCcw, MoveRight, ChevronRight, ChevronLeft } from "lucide-react";
import { OrderButtons } from "./OrderButtons";

// Fields the public booking page collects (matches migration 172).
const PUBLIC_PAGE_KEYS = new Set(["sender_name", "sender_company", "sender_phone", "sender_email",
  "goods_description", "rego_prime_mover"]);

const TabSelect = ({ fd, field, tabKey }) => (
  <div className="sm:col-span-2 flex items-center gap-1">
    <Select value={tabKey} onValueChange={(v) => fd.setDraftValue("field", field.key, "tab_key", v)}>
      <SelectTrigger className="h-9" data-testid={`fd-field-tab-${field.key}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TABS.filter((x) => !x.editOnly).map((x) => (
          <SelectItem key={x.key} value={x.key}>{fd.effective("tab", x.key, "label", x.label)}</SelectItem>
        ))}
        {fd.customTabs.map((x) => (
          <SelectItem key={x.key} value={x.key}>{fd.effective("tab", x.key, "label", x.label)} (custom)</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {tabKey !== field.tab && <MoveRight className="h-3 w-3 text-amber-600" title="Moved from default tab" />}
  </div>
);

const IndentButtons = ({ fd, fieldKey, isFirst, isIndented }) => (
  <div className="sm:col-span-1 flex items-center gap-0.5">
    <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
            onClick={() => fd.toggleIndent(fieldKey, "out")} disabled={!isIndented}
            title="Outdent (make root field)" data-testid={`fd-field-outdent-${fieldKey}`}>
      <ChevronLeft className="h-4 w-4" />
    </Button>
    <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
            onClick={() => fd.toggleIndent(fieldKey, "in")} disabled={isFirst || isIndented}
            title={isFirst ? "Can't indent the first field" : "Indent under field above"}
            data-testid={`fd-field-indent-${fieldKey}`}>
      <ChevronRight className="h-4 w-4" />
    </Button>
  </div>
);

const Toggles = ({ fd, fieldKey, locked }) => (
  <div className="sm:col-span-1 flex items-center gap-2">
    <UsageBadge scope="field" itemKey={fieldKey} />
    <Switch checked={locked || !!fd.effective("field", fieldKey, "is_required", false)} disabled={locked}
            onCheckedChange={(v) => fd.setDraftValue("field", fieldKey, "is_required", v)}
            data-testid={`fd-field-required-${fieldKey}`}
            title={locked ? "Always required (system field)" : "Required — bookers see a red * and can't save until filled"} />
    <Switch checked={locked || !!fd.effective("field", fieldKey, "is_visible", true)} disabled={locked}
            onCheckedChange={(v) => fd.setDraftValue("field", fieldKey, "is_visible", v)}
            data-testid={`fd-field-visible-${fieldKey}`} title={locked ? "Always shown (system field)" : "Visible"} />
  </div>
);

export const FieldRow = ({ fd, field: f, isFirst, isLast }) => {
  const dirty = fd.isDirty("field", f.key);
  const isIndented = !!fd.effective("field", f.key, "group_key", "");
  const set = (prop) => (e) => fd.setDraftValue("field", f.key, prop, e.target.value);
  return (
    <div className={`rounded border ${dirty ? "border-amber-300 bg-amber-50/40" : "border-transparent"} ${isIndented ? "ml-6 border-l-2 border-l-slate-300 pl-3" : ""}`}
         data-testid={`fd-field-row-${f.key}`}>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2">
        <div className="sm:col-span-3 min-w-0">
          <code className="text-xs text-slate-600 break-all">{f.key}</code>
          <div className="text-xs text-slate-400 truncate">{f.label} · {f.component}</div>
          {!f.alwaysRequired && !PUBLIC_PAGE_KEYS.has(f.key) && fd.effective("field", f.key, "is_required", false) && (
            <div className="text-[11px] text-amber-700" data-testid={`fd-field-not-public-${f.key}`}>
              Not on the public booking page; staff are prompted to fill it in when they edit the booking
            </div>
          )}
        </div>
        <div className="sm:col-span-3">
          <Input value={fd.effective("field", f.key, "label", f.label)} onChange={set("label")}
                 placeholder={f.label} className="h-9" data-testid={`fd-field-label-${f.key}`} />
        </div>
        <TabSelect fd={fd} field={f} tabKey={fd.effective("field", f.key, "tab_key", f.tab)} />
        <IndentButtons fd={fd} fieldKey={f.key} isFirst={isFirst} isIndented={isIndented} />
        <OrderButtons onMove={(dir) => fd.moveItem("field", f.key, dir)}
                      isFirst={isFirst} isLast={isLast} kind="field" itemKey={f.key} />
        <Toggles fd={fd} fieldKey={f.key} locked={!!f.alwaysRequired} />
        <div className="sm:col-span-1 text-right">
          <Button size="sm" variant="ghost" onClick={() => fd.resetRow("field", f.key)}
                  disabled={!fd.savedFor("field", f.key) && !dirty}
                  data-testid={`fd-field-reset-${f.key}`}>
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <div className="px-2 pb-2">
        <Input value={fd.effective("field", f.key, "help_text", "") || ""} onChange={set("help_text")}
               placeholder="Help text… (optional, shown under the input in the booking form)"
               className="h-7 text-xs text-slate-600 placeholder:text-slate-400"
               data-testid={`fd-field-help-${f.key}`} />
      </div>
    </div>
  );
};
